<script lang="ts">
  import { imageTypes } from '$lib/constants/media';
  import { getUrlExtension } from 'garden-utils/index';
  import { fadeImageOnLoad } from '$lib/actions/fadeOnLoad';
  import { onMount } from 'svelte';
  import { boolean } from 'zod';

  export let src: string;
  export let lowResSrc: string = null;
  export let controls: boolean = false;
  export let mute: boolean = true;
  export let autoplay: boolean = true;

  let loaded = false;

  let element: HTMLImageElement | HTMLVideoElement = null;

  const type = src?.split('.')[4]?.toUpperCase();

  let check =
    imageTypes.includes(type) || imageTypes.includes(getUrlExtension(src?.toString())) || !type;

  function isImage(blob): Promise<boolean> {
    return new Promise(resolve => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const arr = new Uint8Array(reader.result as ArrayBuffer).subarray(0, 4);
        let header = '';

        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16).padStart(2, '0');
        }

        // Check for common image file headers
        if (/^(89504e47|47494638|ffd8ffe0|ffd8ffe1|ffd8ffe2|ffd8ffe3)/.test(header)) {
          resolve(true); // Image
        } else {
          resolve(false); // Not an image
        }
      };

      reader.onerror = () => {
        resolve(false); // Error reading the blob
      };

      reader.readAsArrayBuffer(blob);
    });
  }

  function fetchAndCheck(url) {
    return fetch(url)
      .then(response => response.blob())
      .then(blob => isImage(blob))
      .then(result => {
        check = result;
      })
      .catch(error => console.error('Error fetching or checking content:', error));
  }

  onMount(async () => {
    if (!element) return;
    if (src.startsWith('blob:')) {
      fetchAndCheck(src);
    }

    if (!check) return;

    if (!lowResSrc) {
      element.src = src;
    } else {
      element.src = lowResSrc;
    }

    element.onload = () => {
      if (loaded) return;
      loaded = true;

      if (!lowResSrc) return;
      // Create a new Image object
      const highResImage = new Image();

      highResImage.src = src;

      highResImage.onload = () => {
        loaded = false;
        if (element) {
          element.src = highResImage.src;
          loaded = true;
        }
      };
    };
  });
</script>

<!-- garden-fullscreen:93756285c6d33f884d0e048dcb464dde55db4dcd15cb536b8f14361d1713ac30:0.png:6538e2905b498c0105c5c504-v2-not-processed -->
{#if check}
  <img
  
    bind:this={element}
    class={`${$$props.class} scrollbar-hide`}
    loading="lazy"
    class:loaded
    alt=""
    use:fadeImageOnLoad
  />
{:else}
  <video
    {controls}
    class={$$props.class}
    preload="metadata"
    on:loadeddata={() => {
      loaded = true;
    }}
    class:loaded
    {autoplay}
    playsinline
    loop
    muted={mute}
    {src}
  />
{/if}

<style lang="postcss">
  img {
    opacity: 0;
    transition: opacity 1200ms ease-out;
  }
  img.loaded {
    opacity: 1;
  }

  video {
    @apply opacity-0;
    transition: opacity 1200ms ease-out;
  }
  video.loaded {
    @apply opacity-100;
  }
</style>
